/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the component responsible for providing the theme to the rest
 * of the application.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Hooks to use state and create side effects.
 */
import { useEffect } from 'react';

/**
 * Hook to get access to the react-router state.
 */
import { useHistory } from 'react-router-dom';

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * hook used to get access to the theme manager.
 */
import useMessageManagerContext from 'src/hooks/useMessageManagerContext';

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const MessageProvider: React.FunctionComponent = ({
    children
}) => {
    // Get the theme manager.
    const messageManager = useMessageManagerContext();

    const history = useHistory();

    // Subscribe to updates from the theme manager and update theme when required.
    useEffect(() => {
        const unsubscribe = messageManager.subscribeToNavigate((navigateData) => {
            if (!navigateData.versionId) {
                return;
            }

            const url = `/${navigateData.versionId}/browse`;

            if (navigateData.gradeId) {
                history.push(`${url}/grade/${navigateData.gradeId}`);
            }
            else if (navigateData.termId) {
                history.push(`${url}/term/${navigateData.termId}`);
            }
            else if (navigateData.categoryId) {
                history.push(`${url}/category/${navigateData.categoryId}`);
            }
            else {
                history.push(url);
            }
        })

        return () => {
            unsubscribe();
        }
    }, [])

    return (
        <>
            {children}
        </>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default MessageProvider;


// WEBPACK FOOTER //
// ./src/components/MessageProvider.tsx